/**
 * Marketing Dashboard — /admin/marketing
 *
 * Human approval queue for the AI marketing agent:
 *   - Agent (marketing_agent/) generates posts and submits them as drafts
 *   - Staff review here: edit caption → Approve or Reject
 *   - Agent polls for approved posts and publishes to Instagram/Facebook
 *
 * Data comes from /api/marketing/* (JWT-protected admin endpoints).
 */

import React, { useCallback, useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const authHeader = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const STATUS_META = {
  pending_review: { label: '🕐 Pending review', bg: '#fef9c3', color: '#92400e' },
  approved:       { label: '👍 Approved',       bg: '#dbeafe', color: '#1d4ed8' },
  publishing:     { label: '🚀 Publishing…',    bg: '#ede9fe', color: '#6d28d9' },
  published:      { label: '✅ Published',      bg: '#f0fdf4', color: '#15803d' },
  rejected:       { label: '🚫 Rejected',       bg: '#f3f4f6', color: '#4b5563' },
  failed:         { label: '❌ Failed',         bg: '#fef2f2', color: '#b91c1c' },
};

const FILTERS = [
  ['pending_review', 'Pending'],
  ['approved', 'Approved'],
  ['published', 'Published'],
  ['rejected', 'Rejected'],
  ['failed', 'Failed'],
  ['', 'All'],
];

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{
      background: m.bg, color: m.color, borderRadius: 20,
      padding: '4px 12px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {m.label}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return '—';
  const s = Math.floor((Date.now() - new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// ── Post card ─────────────────────────────────────────────────────────────────

function PostCard({ post, onAction, busy }) {
  const [caption, setCaption]   = useState(post.caption || '');
  const [hashtags, setHashtags] = useState(post.hashtags || '');
  const [note, setNote]         = useState('');
  const edited = caption !== (post.caption || '') || hashtags !== (post.hashtags || '');
  const reviewable = ['pending_review', 'failed', 'rejected'].includes(post.status);
  const rejectable = ['pending_review', 'approved'].includes(post.status);

  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16,
      boxShadow: '0 1px 4px rgba(0,0,0,.08)', border: '1px solid #e5e7eb',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#1f2937' }}>
            #{post.id} — {post.topic || 'Untitled post'}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            Generated {timeAgo(post.created_at)}
            {post.reviewed_by ? ` · reviewed by ${post.reviewed_by}` : ''}
            {post.published_at ? ` · published ${timeAgo(post.published_at)}` : ''}
          </div>
        </div>
        <StatusBadge status={post.status} />
      </div>

      {/* Image previews */}
      {(post.thumbnails || []).length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          {post.thumbnails.map((src, i) => (
            <img key={i} src={src} alt={`preview ${i + 1}`}
                 style={{ width: 130, height: 130, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
          ))}
        </div>
      )}

      {/* Caption + hashtags */}
      <div style={{ marginTop: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Instagram caption</label>
        <textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          disabled={!reviewable && post.status !== 'approved'}
          rows={5}
          style={{ width: '100%', marginTop: 4, borderRadius: 8, border: '1px solid #d1d5db', padding: 10, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
        />
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Hashtags</label>
        <textarea
          value={hashtags}
          onChange={(e) => setHashtags(e.target.value)}
          disabled={!reviewable && post.status !== 'approved'}
          rows={2}
          style={{ width: '100%', marginTop: 4, borderRadius: 8, border: '1px solid #d1d5db', padding: 10, fontSize: 13, color: '#2563eb', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
        />
      </div>

      {post.error && (
        <div style={{ marginTop: 10, background: '#fef2f2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>
          Publish error: {post.error}
        </div>
      )}
      {post.review_note && post.status === 'rejected' && (
        <div style={{ marginTop: 10, background: '#f3f4f6', color: '#4b5563', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>
          Rejection note: {post.review_note}
        </div>
      )}
      {post.publish_result && (
        <div style={{ marginTop: 10, background: '#f0fdf4', color: '#15803d', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>
          Published: {(post.publish_result.published || []).map((p) => p.platform).join(', ') || 'ok'}
        </div>
      )}

      {/* Actions */}
      {(reviewable || rejectable) && (
        <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          {reviewable && (
            <button
              onClick={() => onAction(post.id, 'approve', { caption, hashtags })}
              disabled={busy}
              style={{ background: '#2C5F2D', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
            >
              {edited ? 'Save edits & Approve' : 'Approve for publishing'}
            </button>
          )}
          {rejectable && (
            <>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Rejection note (optional)"
                style={{ flex: '1 1 180px', borderRadius: 8, border: '1px solid #d1d5db', padding: '9px 12px', fontSize: 13 }}
              />
              <button
                onClick={() => onAction(post.id, 'reject', { review_note: note })}
                disabled={busy}
                style={{ background: '#fff', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
              >
                Reject
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────

export default function MarketingDashboard() {
  const [summary, setSummary] = useState(null);
  const [posts, setPosts]     = useState([]);
  const [logs, setLogs]       = useState([]);
  const [filter, setFilter]   = useState('pending_review');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    try {
      const qs = filter ? `?status=${filter}` : '';
      const [sRes, pRes, lRes] = await Promise.all([
        fetch(`${API_URL}/marketing/summary`, { headers: authHeader() }),
        fetch(`${API_URL}/marketing/posts${qs}`, { headers: authHeader() }),
        fetch(`${API_URL}/marketing/logs?limit=40`, { headers: authHeader() }),
      ]);
      if (!sRes.ok) throw new Error('Could not load marketing data');
      setSummary(await sRes.json());
      setPosts((await pRes.json()).posts || []);
      setLogs((await lRes.json()).logs || []);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [filter]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
  }, [load]);

  const onAction = async (id, action, extra = {}) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/marketing/posts/${id}`, {
        method: 'PATCH',
        headers: authHeader(),
        body: JSON.stringify({ action, ...extra }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || 'Action failed');
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const counts = summary?.counts || {};
  const cards = [
    ['Awaiting review', counts.pending_review, '#92400e', '#fef9c3'],
    ['Approved (queue)', counts.approved, '#1d4ed8', '#dbeafe'],
    ['Published', counts.published, '#15803d', '#f0fdf4'],
    ['Failed', counts.failed, '#b91c1c', '#fef2f2'],
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px', color: '#1f2937' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>📣 Marketing Agent</h2>
        <div style={{ fontSize: 13, color: '#6b7280' }}>
          Agent last active: {summary ? timeAgo(summary.last_agent_activity) : '…'}
          <button onClick={load} style={{ marginLeft: 12, background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{ marginTop: 12, background: '#fef2f2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', fontSize: 14 }}>
          {error} — is the marketing agent configured? (MARKETING_API_KEY on Render, agent running on the clinic machine)
        </div>
      )}

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginTop: 16 }}>
        {cards.map(([label, value, color, bg]) => (
          <div key={label} style={{ background: bg, borderRadius: 12, padding: '14px 16px' }}>
            <div style={{ fontSize: 26, fontWeight: 800, color }}>{value ?? '—'}</div>
            <div style={{ fontSize: 13, color, opacity: 0.85 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
        {FILTERS.map(([value, label]) => (
          <button
            key={label}
            onClick={() => setFilter(value)}
            style={{
              background: filter === value ? '#2C5F2D' : '#fff',
              color: filter === value ? '#fff' : '#374151',
              border: '1px solid #d1d5db', borderRadius: 20,
              padding: '7px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}
          >
            {label}{value && counts[value] != null ? ` (${counts[value]})` : ''}
          </button>
        ))}
      </div>

      {/* Post queue */}
      <div style={{ marginTop: 16 }}>
        {posts.length === 0 && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, textAlign: 'center', color: '#6b7280', border: '1px dashed #d1d5db' }}>
            No posts here. The agent submits new drafts when its pipeline runs
            (<code>python orchestrator.py run</code> on the clinic machine).
          </div>
        )}
        {posts.map((p) => (
          <PostCard key={`${p.id}-${p.status}`} post={p} onAction={onAction} busy={busy} />
        ))}
      </div>

      {/* Activity log */}
      <h3 style={{ marginTop: 28, marginBottom: 8 }}>Agent activity</h3>
      <div style={{ background: '#111827', borderRadius: 12, padding: 16, maxHeight: 260, overflowY: 'auto', fontFamily: 'monospace', fontSize: 12.5 }}>
        {logs.length === 0 && <div style={{ color: '#6b7280' }}>No agent activity yet.</div>}
        {logs.map((l) => (
          <div key={l.id} style={{ color: l.level === 'error' ? '#fca5a5' : '#d1d5db', marginBottom: 4 }}>
            <span style={{ color: '#6b7280' }}>{timeAgo(l.created_at)}</span>
            {' '}<span style={{ color: '#93c5fd' }}>[{l.agent}]</span> {l.message}
          </div>
        ))}
      </div>
    </div>
  );
}
